import startCase from 'lodash/startCase';
import upperFirst from 'lodash/upperFirst';

import {
  Maybe,
  PendingTransitionFragment,
  TransitionStatusFragment,
  WorkflowInstanceCheckFragment,
} from '@spotify/confidence-graphql';

import * as checkUtils from './checks';
import { hasFailingSteps } from './pendingTransitionUtils';
import { WorkflowInstanceStateType } from './types';

type Severity = 'error' | 'warning';
export type Status =
  | 'ok'
  | 'warning'
  | 'error'
  | 'running'
  | 'aborted'
  | 'pending';

type UseWorkflowInstanceStateOptions = {
  state?: WorkflowInstanceStateType;
  checks?: WorkflowInstanceCheckFragment[];
  pendingTransition?: Maybe<PendingTransitionFragment>;
  transitionStatus?: Maybe<TransitionStatusFragment>;
};

export const useWorkflowInstanceState = ({
  state = 'draft',
  checks = [],
  pendingTransition,
  transitionStatus,
}: UseWorkflowInstanceStateOptions = {}) => {
  const checkState = checkUtils.getCheckState(checks);
  const lowerCaseState = state.toLowerCase();
  const severity: Severity = ['live'].includes(lowerCaseState)
    ? 'error'
    : 'warning';

  if (transitionStatus?.failed) {
    const status: Status = 'error';
    const label = `Error when starting transition: ${transitionStatus.failed.error}`;
    return { status, label };
  } else if (!pendingTransition && transitionStatus?.running) {
    const status: Status = 'running';
    const label = ` ${startCase(transitionStatus.action)} in progress`;
    return { status, label };
  }

  let label = startCase(lowerCaseState);
  let status: Status = 'pending';

  if (!['archived', 'ended'].includes(lowerCaseState)) {
    if (pendingTransition) {
      if (hasFailingSteps(pendingTransition)) {
        label = `Failed to ${pendingTransition.transitionAction}`;
      } else {
        label = `${upperFirst(pendingTransition.transitionAction)} in progress`;
      }
    } else if (checkUtils.checkFailed(checkState)) {
      label += ' w/ failing checks';
    }
  }

  if (pendingTransition && hasFailingSteps(pendingTransition)) {
    status = severity === 'warning' ? 'warning' : 'error';
  } else if (['archived', 'ended'].includes(lowerCaseState)) {
    status = 'aborted';
  } else if (['draft'].includes(lowerCaseState)) {
    if (checkUtils.checkFailed(checkState)) {
      status = 'warning';
    } else {
      status = 'running';
    }
  } else if (['live', 'active'].includes(lowerCaseState)) {
    if (checkUtils.checkFailed(checkState)) {
      status = 'error';
    } else {
      status = 'ok';
    }
  } else if (checkUtils.checkFailed(checkState)) {
    status = 'warning';
  }

  return { status, label };
};
